import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useParams } from 'wouter';
import { api } from '../lib/api';
import { applicationLabels, money, orderLabels, shortDate } from '../lib/format';
import { fieldErrors } from '../lib/form-errors';
import { haptic } from '../lib/telegram';
import type { ClientCard } from '../types';
import {
  Button,
  Card,
  ErrorNotice,
  Field,
  Input,
  LoadingScreen,
  Modal,
  PageHeader,
  StatusBadge,
} from '../components/ui';

const CLIENT_TAGS_LIMIT = 8;

export function ClientPage() {
  const { id = '' } = useParams<{ id: string }>();
  const [editing, setEditing] = useState(false);
  const query = useQuery({
    queryKey: ['client', id],
    queryFn: () => api<ClientCard>(`/clients/${id}`),
  });

  if (query.isPending) return <LoadingScreen label="Поднимаем историю" />;
  if (query.error) return <ErrorNotice error={query.error} />;
  const client = query.data;

  return (
    <>
      <PageHeader
        eyebrow="Клиент"
        title={client.displayName}
        action={
          <Button variant="ghost" onClick={() => setEditing(true)}>
            Изменить
          </Button>
        }
      />
      <div className="order-hero">
        {client.isBlocked && <StatusBadge tone="danger">Заблокирован</StatusBadge>}
        <p>
          {client.username ? `@${client.username}` : 'Без ника'}
          {client.telegramId ? '' : ' · заявки из Telegram к нему не привяжутся'}
        </p>
        {client.tags.length > 0 && (
          <div className="tag-list" aria-label="Теги клиента">
            {client.tags.map((tag) => (
              <span key={tag} className="tag-pill">
                {tag}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Валюты не складываются: по строке на каждую, как и в заказах. */}
      <div className="section-heading">
        <h2>Деньги</h2>
      </div>
      <Card className="stack stack--compact">
        {client.money.length === 0 ? (
          <p className="muted">Денег по этому человеку пока не проходило.</p>
        ) : (
          client.money.map((row) => (
            <div key={row.currency} className="detail-grid">
              <div>
                <span>Получено, {row.currency}</span>
                <strong>{money(row.paid, row.currency)}</strong>
              </div>
              <div>
                <span>К получению</span>
                <strong className={Number(row.due) > 0 ? 'text-danger' : ''}>
                  {money(row.due, row.currency)}
                </strong>
              </div>
            </div>
          ))
        )}
        {Number(client.refunded) > 0 && <p className="meta">Возвращено: {client.refunded}</p>}
      </Card>

      {client.notes && (
        <>
          <div className="section-heading">
            <h2>Заметки</h2>
          </div>
          <Card>
            <div className="prose-block prose-block--private">
              <span>Видно только вам</span>
              <p>{client.notes}</p>
            </div>
          </Card>
        </>
      )}

      <div className="section-heading">
        <h2>Заказы</h2>
        <span className="meta">{client.orders.length}</span>
      </div>
      {client.orders.length === 0 ? (
        <Card>
          <p className="muted">Вы с ним ещё не работали.</p>
        </Card>
      ) : (
        <div className="list">
          {client.orders.map((order) => (
            <Link key={order.id} href={`/orders/${order.id}`} className="card order-card">
              <div className="card-row">
                <StatusBadge
                  tone={
                    order.status === 'IN_PROGRESS'
                      ? 'accent'
                      : order.status === 'COMPLETED'
                        ? 'success'
                        : 'neutral'
                  }
                >
                  {orderLabels[order.status]}
                </StatusBadge>
                <span className="meta">{order.publicNumber}</span>
              </div>
              <h2>{order.title}</h2>
              <div className="card-row card-row--bottom">
                <span>{shortDate(order.deadline)}</span>
                <strong>{money(order.totalPrice, order.currency)}</strong>
              </div>
            </Link>
          ))}
        </div>
      )}

      <div className="section-heading">
        <h2>Заявки</h2>
        <span className="meta">{client.applications.length}</span>
      </div>
      <Card>
        {client.applications.length === 0 ? (
          <p className="muted">Заявок от него пока не приходило.</p>
        ) : (
          <div className="timeline">
            {client.applications.map((application) => (
              <div className="timeline__item" key={application.id}>
                <span className="timeline__dot" />
                <div>
                  <strong>{application.title}</strong>
                  <p>
                    {applicationLabels[application.status]} · {shortDate(application.submittedAt)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {editing && <EditClientModal client={client} onClose={() => setEditing(false)} />}
    </>
  );
}

/**
 * Блокировка живёт здесь же: заблокированный клиент не сможет отправить новую
 * публичную заявку, но уже существующие заказы у него остаются.
 */
function EditClientModal({ client, onClose }: { client: ClientCard; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [displayName, setDisplayName] = useState(client.displayName);
  const [username, setUsername] = useState(client.username ?? '');
  const [notes, setNotes] = useState(client.notes ?? '');
  const [tags, setTags] = useState(client.tags.join(', '));
  const [isBlocked, setIsBlocked] = useState(client.isBlocked);
  const parsedTags = [
    ...new Set(
      tags
        .split(',')
        .map((tag) => tag.trim().toLowerCase())
        .filter(Boolean),
    ),
  ];
  const tagsError =
    parsedTags.length > CLIENT_TAGS_LIMIT ? `Не больше ${CLIENT_TAGS_LIMIT} тегов` : undefined;
  const mutation = useMutation({
    mutationFn: () =>
      api<ClientCard>(`/clients/${client.id}`, {
        method: 'PATCH',
        body: {
          displayName: displayName.trim(),
          username: username.trim() || null,
          notes: notes.trim() || null,
          tags: parsedTags,
          isBlocked,
        },
      }),
    onSuccess: () => {
      haptic('success');
      void queryClient.invalidateQueries({ queryKey: ['client', client.id] });
      void queryClient.invalidateQueries({ queryKey: ['clients'] });
      onClose();
    },
  });
  const errors = fieldErrors(mutation.error);

  return (
    <Modal title="Клиент" onClose={onClose}>
      <form
        // Ошибки показывают поля формы: браузерная подсказка дёргала лист.
        noValidate
        className="stack"
        onSubmit={(event) => {
          event.preventDefault();
          mutation.mutate();
        }}
      >
        <Field label="Имя" error={errors.displayName}>
          <Input value={displayName} onChange={(event) => setDisplayName(event.target.value)} />
        </Field>
        <Field label="Ник в Telegram" error={errors.username}>
          <Input
            value={username}
            placeholder="@anna"
            onChange={(event) => setUsername(event.target.value)}
          />
        </Field>
        <Field
          label="Теги"
          hint={`До ${CLIENT_TAGS_LIMIT} пометок через запятую — «постоянный», «оплата вперёд». Видно только вам`}
          error={tagsError ?? errors.tags}
        >
          <Input
            value={tags}
            placeholder="постоянный, предоплата обязательна"
            onChange={(event) => setTags(event.target.value)}
          />
        </Field>
        <Field label="Заметки" hint="Видно только вам" error={errors.notes}>
          <textarea
            className="input input--textarea"
            rows={3}
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
          />
        </Field>
        <label className="toggle-row">
          <span>Заблокирован</span>
          <button
            type="button"
            role="switch"
            aria-checked={isBlocked}
            aria-label="Заблокирован"
            className={`switch ${isBlocked ? 'is-on' : ''}`}
            onClick={() => setIsBlocked(!isBlocked)}
          >
            <span />
          </button>
        </label>
        <p className="muted settings-note">
          Заблокированный человек больше не отправит вам заявку. Текущие заказы и переписка
          останутся.
        </p>
        {mutation.error && <ErrorNotice error={mutation.error} />}
        <Button
          type="submit"
          wide
          disabled={!displayName.trim() || !!tagsError || mutation.isPending}
        >
          {mutation.isPending ? 'Сохраняем…' : 'Сохранить'}
        </Button>
      </form>
    </Modal>
  );
}
